// Response/request shapes of the wave-3 surface, mirrored from apps/api/src/schemas-{integrations,
// browser,memory}.ts and the route handlers (apps/api/src/routes/{integrations,browser,memory}.ts).
// Secrets never appear here: only their masked state.
import type { SecretState } from "./types-wave2.ts";

export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

// ---------- Integrations (F06) ----------

/** Kinds of the manual registration form; `github_token` connections are created by "Connect GitHub" (D21 G1). */
export const INTEGRATION_KINDS = ["github_app", "sentry"] as const;
export type IntegrationKind = (typeof INTEGRATION_KINDS)[number] | "github_token";
export type IntegrationState = "draft" | "active" | "suspended" | "disabled";
export type IntegrationSecretField = "webhook_secret" | "app_private_key" | "token";
export const AUTHOR_ASSOCIATIONS = ["OWNER", "MEMBER", "COLLABORATOR"] as const;

export interface GithubCommentPolicy {
  allowed_user_ids?: number[];
  allowed_logins?: string[];
  allowed_associations?: string[];
}

export interface Integration {
  id: string;
  organization_id: string;
  kind: IntegrationKind;
  name: string;
  external_ref: string;
  config: Record<string, unknown>;
  state: IntegrationState;
  version: number;
  created_by: string | null;
  secrets: Record<IntegrationSecretField, SecretState>;
  /** null for token connections (no webhook of their own). */
  webhook_path: string | null;
  /** allowlisted: real work allowed for this id; setup_allowed: GitHub connection steps allowed (D21 G1). */
  real_network: { allowlisted: boolean; setup_allowed?: boolean; status: "BLOCKED-external" | "allowed" };
  created_at: string;
  updated_at: string;
}

// ---------- GitHub connections (D21 G1) ----------

export interface GithubTestStep {
  step: string;
  state: "passed" | "failed" | "not_run";
  detail?: string;
}
export interface GithubRepository {
  fullName: string;
  private: boolean;
  defaultBranch: string | null;
  /** Repository size in KB as GitHub reports it (null when unknown). */
  sizeKb: number | null;
}
export interface GithubConnectionTest {
  integration: Integration;
  result: "ok" | "failed";
  steps: GithubTestStep[];
  repositories: { total: number; items: GithubRepository[] } | null;
}
export interface GithubManifestStart {
  state: string;
  expires_at: string;
  post_url: string;
  manifest: Record<string, unknown>;
}

export interface OwnershipProof {
  method: "manual_attestation";
  statement: string;
}

export interface CreateIntegrationInput {
  kind: IntegrationKind;
  name: string;
  external_ref: string;
  config?: Record<string, unknown>;
  webhook_secret?: string;
  app_private_key?: string;
  /** github_token: write-only personal access token. */
  token?: string;
  accept_classic_token?: boolean;
  ownership_proof?: OwnershipProof;
}

export interface PatchIntegrationInput {
  expected_version: number;
  name?: string;
  external_ref?: string;
  config?: Record<string, unknown>;
}

export interface IntegrationMapping {
  id: string;
  organization_id: string;
  integration_id: string;
  project_id: string;
  external_project_ref: string;
  environments: string[];
  state: "active" | "disabled";
  /** D21 G2: null ⇒ pull request delivery off (default). */
  pr_delivery_enabled_at: string | null;
  pr_max_per_hour: number;
  created_at: string;
}

export const INBOX_STATES = ["received", "processing", "processed", "failed", "dead_letter", "ignored"] as const;
export type InboxState = (typeof INBOX_STATES)[number];
/** Requeueable states (packages/db REQUEUEABLE_STATES): reprocess/assign are refused otherwise. */
export const REQUEUEABLE_INBOX_STATES: readonly InboxState[] = ["processed", "failed", "dead_letter", "ignored"];

export interface InboxEventSummary {
  id: string;
  project_id: string | null;
  integration_id: string;
  source: "github" | "sentry";
  delivery_id: string;
  event_type: string;
  state: InboxState;
  attempts: number;
  reprocess_count: number;
  last_error: string | null;
  dead_letter_reason: string | null;
  ordering_key: string | null;
  correlation_id: string | null;
  received_at: string;
  occurred_at: string | null;
  processed_at: string | null;
  next_attempt_at: string | null;
}

export interface InboxEventDetail extends InboxEventSummary {
  payload_sha256: string;
  headers: Record<string, string>;
  payload: Record<string, unknown>;
}

export interface InboxQuery {
  state?: InboxState | "";
  integration_id?: string;
  project_id?: string;
  unassigned?: boolean;
  cursor?: string | null;
  limit?: number;
}

export const INCIDENT_STATES = ["open", "linked", "resolved", "ignored", "regressed"] as const;
export type IncidentState = (typeof INCIDENT_STATES)[number];

export interface Incident {
  id: string;
  project_id: string | null;
  integration_id: string;
  task_id: string | null;
  fingerprint: string;
  title: string;
  level: string | null;
  environment: string | null;
  release_ref: string | null;
  state: IncidentState;
  event_count: number;
  first_seen_at: string;
  last_seen_at: string;
  created_at: string;
}

export interface PullRequestLink {
  id: string;
  project_id: string;
  task_id: string;
  attempt_id: string | null;
  integration_id: string;
  repository: string;
  pr_number: number;
  url: string | null;
  base_branch: string | null;
  head_sha: string | null;
  head_seq: number;
  state: "open" | "closed" | "merged";
  created_at: string;
  updated_at: string;
}

// ---------- Browser / preview (F05) ----------

export const URL_FILTER_LABEL = "URL filter, not network isolation";

export type BrowserSecretState = { state: SecretState["state"] };

export interface TestAccount {
  id: string;
  project_id: string;
  environment_id: string;
  label: string;
  username: string;
  allowed_hosts: string[];
  state: "active" | "disabled";
  version: number;
  secrets: { password: BrowserSecretState; totp: BrowserSecretState };
  created_at: string;
  updated_at: string;
}

export interface CreateTestAccountInput {
  environment_id: string;
  label: string;
  username: string;
  password: string;
  totp_secret?: string;
  allowed_hosts: string[];
}

export interface PatchTestAccountInput {
  expected_version: number;
  label?: string;
  username?: string;
  allowed_hosts?: string[];
  state?: "active" | "disabled";
}

export type ScenarioState = "draft" | "active" | "retired";

export interface BrowserScenario {
  id: string;
  project_id: string;
  scenario_key: string;
  version: number;
  body: Record<string, unknown>;
  body_hash: string;
  environment_id: string | null;
  test_account_id: string | null;
  state: ScenarioState;
  created_by?: string | null;
  created_at: string;
  egress_control_label: string;
}

export type BrowserRunStatus = "queued" | "running" | "passed" | "failed" | "aborted" | "paused" | "rejected";

export interface BrowserRun {
  id: string;
  project_id: string;
  scenario_id: string;
  task_id: string | null;
  attempt_id: string | null;
  environment_id: string | null;
  mode: "local_fixture" | "mock" | "staging";
  status: BrowserRunStatus;
  failure_class: string | null;
  retryable: boolean | null;
  blocked_requests: { host: string | null; port: number | null; reason: string; resource_type: string; is_navigation: boolean }[];
  trace_artifact_sha256: string | null;
  trace_artifact_id: string | null;
  trace_download_url: string | null;
  evidence_label: string;
  egress_control_label: string;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
}

export interface BrowserSession {
  id: string;
  project_id: string;
  test_account_id: string;
  state: "valid" | "expired" | "revoked" | "invalid";
  storage_state: { stored_in: "vault"; state: SecretState["state"] };
  obtained_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  expires_at_note?: string;
}

export type EmbedPolicy = "embeddable" | "new_tab_only" | "capture_only";

export interface PreviewTarget {
  id: string;
  project_id?: string;
  url: string;
  environment_id: string | null;
  task_id: string | null;
  state: "fresh" | "stale" | "unreachable";
  display_state: "preparing" | "available" | "stale" | "unavailable";
  stale: boolean;
  stale_reason: string | null;
  version: { previewed_candidate_hash: string | null; current_candidate_hash: string | null; matches: boolean | null };
  embed: { policy: EmbedPolicy; reason: string; source: string; headers_source: "declared" | "probe_local_fixture" | null; detail?: unknown };
  live_embed_allowed: boolean;
  alternatives: ("open_in_new_tab" | "view_capture")[];
  capture: {
    evidence_id: string;
    artifact_id: string | null;
    artifact_sha256: string | null;
    candidate_hash: string | null;
    captured_at: string;
    download_url: string | null;
  } | null;
  checked_at: string | null;
  egress_control_label: string;
  note?: string;
}

export interface PreviewResponse {
  project_id: string;
  environment_id: string | null;
  panel_origin: string;
  egress_control_label: string;
  items: PreviewTarget[];
}

export interface CreatePreviewTargetInput {
  url: string;
  environment_id: string | null;
  task_id: string | null;
}

// ---------- Memory (F07) ----------

export const MEMORY_SOURCE_KINDS = ["markdown", "prd", "code", "text"] as const;
export const MEMORY_SOURCE_STATES = ["candidate", "validated", "obsolete", "rejected"] as const;
export const MEMORY_STRATEGIES = ["none", "text", "vector", "hybrid", "hybrid+rerank"] as const;
export const MEMORY_BUILD_STRATEGIES = ["text", "vector", "hybrid", "hybrid+rerank"] as const;
export type MemorySourceState = (typeof MEMORY_SOURCE_STATES)[number];
export type MemoryStrategy = (typeof MEMORY_STRATEGIES)[number];

export interface MemorySource {
  id: string;
  project_id: string;
  source_key: string;
  kind: (typeof MEMORY_SOURCE_KINDS)[number];
  version: number;
  content_hash: string;
  origin_commit: string | null;
  is_current: boolean;
  superseded_by: string | null;
  state: MemorySourceState;
  classification: "public" | "internal" | "confidential";
  author: string;
  created_at: string;
  label: "current" | "historical";
}

export interface MemorySourceDetail extends MemorySource {
  versions: MemorySource[];
}

export interface MemorySourcesPage {
  project_id: string;
  items: MemorySource[];
  next_cursor: string | null;
}

export type IngestInput =
  | { origin: "prd_version"; prd_version_id: string }
  | { origin: "decision"; key: string; title: string; body: string; evidence_refs: string[]; validated: boolean; classification?: "public" | "internal" | "confidential" }
  | { origin: "repository"; workspace_id: string; commit: string; paths: string[]; classification?: "public" | "internal" | "confidential" };

export interface IngestResponse {
  origin: "prd_version" | "decision" | "repository";
  commit: string | null;
  results: {
    source_key: string;
    outcome: "created" | "unchanged" | "refused";
    reason: string | null;
    detail: string | null;
    source: MemorySource | null;
    superseded_id: string | null;
    chunks: number | null;
  }[];
}

export interface TombstoneResult {
  source_key: string;
  versions: number;
  chunks_removed: number;
  embeddings_removed: number;
  retrieval_items_removed: number;
  tombstones: number;
}

export interface MemoryHit {
  rank: number;
  chunk_id: string;
  source_id: string;
  source_key: string;
  source_version: number;
  index_version_id: string;
  label: "current" | "historical";
  historical: boolean;
  superseded_by: string | null;
  score: number;
  scores: Record<string, number>;
  symbols: string[];
  excerpt: string;
  token_estimate: number;
  provenance: {
    project_id: string;
    source_ref: string;
    origin_commit: string | null;
    author: string;
    state: MemorySourceState;
    classification: string;
    ordinal: number;
    start_line: number | null;
    end_line: number | null;
  };
  wrapped: string;
}

export interface MemorySearchInput {
  query: string;
  k?: number;
  include_historical?: boolean;
  strategy?: MemoryStrategy;
  index_version_id?: string;
}

export interface MemorySearchResponse {
  retrieval_id: string;
  index_version_id: string;
  strategy: MemoryStrategy;
  filters: { project_id: string; index_version_id: string; include_historical: boolean; eligible_chunks: number | null };
  hits: MemoryHit[];
  operations: Record<string, number>;
  latency_ms: number;
  notice: string;
  /** Semantic stages that failed; results then fell back to text (AC41). Empty when nothing degraded. */
  degraded: MemoryDegradation[];
}

export interface MemoryDegradation {
  stage: "embed" | "rerank";
  reason: string;
}

export interface MemoryStats {
  project_id: string;
  sources: {
    current: number;
    versions: number;
    historical_versions: number;
    by_kind: Record<string, number>;
    by_state: Record<string, number>;
    without_evidence: number;
    obsolete: number;
    rejected: number;
    tombstoned_keys: number;
    oldest_current_at: string | null;
    newest_at: string | null;
  };
  index: {
    active_index_version_id: string | null;
    chunks: number;
    content_bytes: number;
    duplicate_source_groups: number;
    conflicting_symbols: number;
    conflicting_symbol_samples: string[];
  };
  retrievals: {
    total: number;
    without_results: number;
    latency_ms_mean: number | null;
    latency_ms_p95: number | null;
    inserted_tokens_estimate: number;
  };
  retrieval_cost: { currency: string; total: number; note: string };
}

export type IndexVersionStatus = "building" | "ready" | "active" | "retired" | "failed";

export interface MemoryIndexVersion {
  id: string;
  project_id: string;
  status: IndexVersionStatus;
  chunking_version: string;
  strategy: MemoryStrategy;
  embedder: { provider: string; model: string; version: string; dimension: number } | null;
  reranker: { provider: string; model: string; version: string } | null;
  embedding_dimension: number | null;
  evaluation: Record<string, unknown> | null;
  failed_reason: string | null;
  previous_active_id: string | null;
  serving: boolean;
  created_at: string;
  ready_at: string | null;
  activated_at: string | null;
  retired_at: string | null;
}

export interface MemoryDataset {
  id: string;
  project_id: string;
  name: string;
  version: number;
  body_hash: string;
  state: "draft" | "frozen" | "retired";
  created_by: string | null;
  created_at: string;
  case_count: number;
}

export interface MemoryDatasetCase {
  case_key: string;
  question: string;
  expected: { source_key: string; symbol?: string }[];
  forbidden_source_keys: string[];
  stale_source_keys: string[];
}

export interface MemoryEvaluation {
  id: string;
  project_id: string;
  dataset_id: string;
  index_version_id: string;
  baseline_index_version_id: string | null;
  strategy: MemoryStrategy;
  k: number;
  recall_at_k: number | null;
  contamination_rate: number | null;
  stale_as_current: number | null;
  report_hash: string | null;
  report: Record<string, unknown> | null;
  state: "running" | "completed" | "failed";
  created_at: string;
  completed_at: string | null;
}

export interface MemoryEvaluationAccepted {
  operation_id: string;
  evaluation_id: string;
  status: "accepted";
  status_url: string;
}

export interface MemoryComparison {
  dataset_id: string;
  target: Omit<MemoryEvaluation, "report">;
  baseline: Omit<MemoryEvaluation, "report"> | null;
  delta: { recall_at_k: number | null; contamination_rate: number | null; stale_as_current: number | null };
  promotable: boolean;
  failures: string[];
}

// ---------- Coolify connection test (wave 3: may be real when allowlisted) ----------

/** Success/failure shape of POST /v1/infrastructure/coolify-connections/:id/test (read-only, allowlisted). */
export interface CoolifyTestResult {
  status?: string;
  failure_class?: string | null;
  latency_ms?: number | null;
  steps?: { step: string; state: string; detail?: unknown; latency_ms?: number | null }[];
  detail?: Record<string, unknown>;
  capabilities?: unknown;
  [key: string]: unknown;
}
